/**
 * i18n (Internationalization) Utility
 * 
 * Provides runtime translation support with centralized message catalogs.
 * 
 * Features:
 * - Runtime locale switching
 * - Parameter interpolation: t('key', { name: 'value' })
 * - Nested key access via dot notation
 * - LocalStorage persistence
 * - Browser language detection
 * - Graceful fallback to English
 * 
 * Usage:
 *   import { t, setLocale, getLocale } from './utils/i18n.js';
 *   
 *   const greeting = t('common.welcome', { name: 'Alice' });
 *   setLocale('de'); // Switch to German
 */

import en from '../../locales/en.json';
import de from '../../locales/de.json';

type Messages = Record<string, any>;
type Locale = 'en' | 'de';

const SUPPORTED_LOCALES: Locale[] = ['en', 'de'];
const DEFAULT_LOCALE: Locale = 'en';
const STORAGE_KEY = 'locale';

// Message catalogs
const messages: Record<Locale, Messages> = {
    en,
    de
};

// Current active locale
let currentLocale: Locale = DEFAULT_LOCALE;

/**
 * Get value from nested object using dot notation
 * Example: get({ a: { b: 'value' } }, 'a.b') => 'value'
 */
function getNestedValue(obj: any, path: string): string | undefined {
    const keys = path.split('.');
    let current = obj;
    
    for (const key of keys) {
        if (current === null || current === undefined) {
            return undefined;
        }
        current = current[key];
    }
    
    return typeof current === 'string' ? current : undefined;
}

/**
 * Interpolate parameters into a string
 * Example: interpolate('Hello, {name}!', { name: 'Alice' }) => 'Hello, Alice!'
 */
function interpolate(template: string, params?: Record<string, string | number>): string {
    if (!params) return template;
    
    return template.replace(/\{(\w+)\}/g, (match, key) => {
        return params[key] !== undefined ? String(params[key]) : match;
    });
}

/**
 * Translate a message key to the current locale
 * 
 * @param key - Dot-notation key path (e.g., 'login.title')
 * @param params - Optional parameters for interpolation
 * @returns Translated string or key if not found
 */
export function t(key: string, params?: Record<string, string | number>): string {
    const catalog = messages[currentLocale];
    const translation = getNestedValue(catalog, key);
    
    if (translation === undefined) {
        // Fallback to English if not found in current locale
        if (currentLocale !== DEFAULT_LOCALE) {
            const fallback = getNestedValue(messages[DEFAULT_LOCALE], key);
            if (fallback !== undefined) {
                return interpolate(fallback, params);
            }
        }
        
        // Return key as last resort
        return key;
    }
    
    return interpolate(translation, params);
}

/**
 * Get current active locale
 */
export function getLocale(): Locale {
    return currentLocale;
}

/**
 * Set active locale and persist to localStorage
 * 
 * @param locale - Locale code to activate
 * @returns true if locale was changed, false if unsupported
 */
export function setLocale(locale: string): boolean {
    // Normalize and validate
    const normalized = locale.toLowerCase().split('-')[0] as Locale;
    
    if (!SUPPORTED_LOCALES.includes(normalized)) {
        console.warn(`Unsupported locale: ${locale}, falling back to ${DEFAULT_LOCALE}`);
        return false;
    }
    
    currentLocale = normalized;
    localStorage.setItem(STORAGE_KEY, normalized);
    
    // Dispatch event for components to react to locale change
    window.dispatchEvent(new CustomEvent('localechange', { detail: { locale: normalized } }));
    
    return true;
}

/**
 * Detect and load locale from localStorage or browser settings
 * Call this on app initialization
 */
export function loadLocale(): void {
    // 1. Check localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) {
        currentLocale = stored as Locale;
        return;
    }
    
    // 2. Detect from browser
    const browserLang = navigator.language || (navigator as any).userLanguage;
    if (browserLang) {
        const lang = browserLang.toLowerCase().split('-')[0];
        if (SUPPORTED_LOCALES.includes(lang as Locale)) {
            currentLocale = lang as Locale;
            localStorage.setItem(STORAGE_KEY, lang);
            return;
        }
    }
    
    // 3. Default to English
    currentLocale = DEFAULT_LOCALE;
    localStorage.setItem(STORAGE_KEY, DEFAULT_LOCALE);
}

/**
 * Get list of supported locales with display names
 */
export function getSupportedLocales(): Array<{ code: Locale; name: string }> {
    return [
        { code: 'en', name: 'English' },
        { code: 'de', name: 'Deutsch' }
    ];
}
